import { UserRole } from '@/school-management/types/roles';

/**
 * Centralized route configuration
 * This file contains all application routes in one place for easy maintenance
 */

export const ROUTES = {
  // Authentication
  LOGIN: '/login',

  // Main Pages
  ACCOUNTS: '/accounts',
  STATES: '/states',
  REGIONS: '/regions',
  SCHOOLS: '/schools',
  DISTRICTS: '/districts',
  HISTORY: '/history',
  USER_MANAGEMENT: '/user-management',
  STYLE_GUIDE: '/style-guide',

  // Account Management
  CREATE_ACCOUNT: '/create-account',
  EDIT_ACCOUNT: (accountId: string) => `/edit-account/${accountId}`,

  // School Management
  SCHOOL_DETAILS: (schoolId: string) => `/schools/${schoolId}`,
  EDIT_TEACHER: (schoolId: string, teacherId: string) =>
    `/schools/${schoolId}/edit-teacher/${teacherId}`,
  CREATE_TEACHER: (schoolId: string) => `/schools/${schoolId}/create-teacher`,
  CREATE_CLASS: (schoolId: string) => `/schools/${schoolId}/create-class`,
  EDIT_CLASS: (schoolId: string, classId: string) => `/schools/${schoolId}/edit-class/${classId}`,
  VIEW_CLASS: (schoolId: string, classId: string) => `/schools/${schoolId}/class/${classId}`,

  // District Management
  DISTRICT_DETAILS: (districtId: string) => `/districts/${districtId}`,
  DISTRICT_EDIT: (districtId: string) => `/districts/${districtId}/edit`,
  ADD_DISTRICT: '/districts/add',
  ADD_DISTRICT_ALT: '/add-district',
  EDIT_DISTRICT: (districtId: string) => `/edit-district/${districtId}`,

  // School Management (Alternative routes)
  ADD_SCHOOL: '/add-school',

  // Account Management (Legacy - for backward compatibility)
  ACCOUNT_DETAILS: (accountId: string) => `/accounts/${accountId}`,
  ACCOUNT_DETAILS_PAGE: '/AccountDetailsPage',

  // Route Patterns for Router and Access Control
  PATTERNS: {
    // School dynamic routes
    SCHOOL_DETAILS: '/schools/:schoolId',
    EDIT_TEACHER: '/schools/:schoolId/edit-teacher/:teacherId',
    CREATE_TEACHER: '/schools/:schoolId/create-teacher',
    CREATE_CLASS: '/schools/:schoolId/create-class',
    EDIT_CLASS: '/schools/:schoolId/edit-class/:classId',
    VIEW_CLASS: '/schools/:schoolId/class/:classId',

    // District dynamic routes
    DISTRICT_DETAILS: '/districts/:districtId',
    DISTRICT_EDIT: '/districts/:districtId/edit',
    EDIT_DISTRICT: '/edit-district/:districtId',

    // Account dynamic routes
    ACCOUNT_DETAILS: '/accounts/:accountId',
    EDIT_ACCOUNT: '/edit-account/:accountId',
  },
} as const;

/**
 * Helper functions for generating routes with query parameters
 */
export const ROUTE_WITH_QUERY = {
  SCHOOL_DETAILS_WITH_TAB: (schoolId: string, tab: string) => `/schools/${schoolId}?tab=${tab}`,
} as const;

/**
 * Helper function to generate dynamic routes
 * Usage: ROUTES.EDIT_ACCOUNT('123') -> '/edit-account/123'
 */
export const generateRoute = (route: keyof typeof ROUTES, ...params: string[]) => {
  const routeValue = ROUTES[route];
  if (typeof routeValue === 'function') {
    // Type assertion to handle different function signatures
    return (routeValue as any)(...params);
  }
  return routeValue;
};

/**
 * Route groups for better organization
 */
export const ROUTE_GROUPS = {
  ACCOUNT: {
    LIST: ROUTES.ACCOUNTS,
    CREATE: ROUTES.CREATE_ACCOUNT,
    EDIT: ROUTES.EDIT_ACCOUNT,
    DETAILS: ROUTES.ACCOUNT_DETAILS,
  },
  SCHOOL: {
    LIST: ROUTES.SCHOOLS,
    DETAILS: ROUTES.SCHOOL_DETAILS,
    EDIT_TEACHER: ROUTES.EDIT_TEACHER,
    CREATE_TEACHER: ROUTES.CREATE_TEACHER,
    CREATE_CLASS: ROUTES.CREATE_CLASS,
    EDIT_CLASS: ROUTES.EDIT_CLASS,
  },
  DISTRICT: {
    LIST: ROUTES.DISTRICTS,
    DETAILS: ROUTES.DISTRICT_DETAILS,
    ADD: ROUTES.ADD_DISTRICT,
    EDIT: ROUTES.DISTRICT_EDIT,
  },
} as const;

/**
 * Route access configuration
 * Defines which roles can access which routes
 * This ensures consistency between routing and access control
 */
export const ROUTE_ACCESS_RULES: Record<string, UserRole[]> = {
  // Account Management - Only SuperAdmin and AccountManager
  [ROUTES.ACCOUNTS]: [UserRole.SuperAdmin, UserRole.AccountManager],
  [ROUTES.CREATE_ACCOUNT]: [UserRole.SuperAdmin, UserRole.AccountManager],
  [ROUTES.PATTERNS.ACCOUNT_DETAILS]: [UserRole.SuperAdmin, UserRole.AccountManager],
  [ROUTES.PATTERNS.EDIT_ACCOUNT]: [UserRole.SuperAdmin, UserRole.AccountManager],

  // School Management - SuperAdmin, RegionManager, Teacher
  [ROUTES.SCHOOLS]: [UserRole.SuperAdmin, UserRole.RegionManager, UserRole.Teacher],
  [ROUTES.PATTERNS.SCHOOL_DETAILS]: [
    UserRole.Admin,
    UserRole.SuperAdmin,
    UserRole.RegionManager,
    UserRole.Teacher,
  ],
  [ROUTES.PATTERNS.EDIT_TEACHER]: [UserRole.Admin, UserRole.SuperAdmin, UserRole.RegionManager],
  [ROUTES.PATTERNS.CREATE_TEACHER]: [UserRole.Admin, UserRole.SuperAdmin, UserRole.RegionManager],
  [ROUTES.PATTERNS.CREATE_CLASS]: [UserRole.Admin, UserRole.SuperAdmin, UserRole.RegionManager],
  [ROUTES.PATTERNS.EDIT_CLASS]: [UserRole.Admin, UserRole.SuperAdmin, UserRole.RegionManager],
  [ROUTES.PATTERNS.VIEW_CLASS]: [
    UserRole.Admin,
    UserRole.SuperAdmin,
    UserRole.RegionManager,
    UserRole.Teacher,
  ],

  // District Management - Admin, SuperAdmin, RegionManager
  [ROUTES.DISTRICTS]: [UserRole.Admin, UserRole.SuperAdmin, UserRole.RegionManager],
  [ROUTES.ADD_DISTRICT]: [UserRole.Admin, UserRole.SuperAdmin, UserRole.RegionManager],
  [ROUTES.ADD_DISTRICT_ALT]: [UserRole.Admin, UserRole.SuperAdmin, UserRole.RegionManager],
  [ROUTES.PATTERNS.DISTRICT_DETAILS]: [UserRole.Admin, UserRole.SuperAdmin, UserRole.RegionManager],
  [ROUTES.PATTERNS.DISTRICT_EDIT]: [UserRole.Admin, UserRole.SuperAdmin, UserRole.RegionManager],
  [ROUTES.PATTERNS.EDIT_DISTRICT]: [UserRole.Admin, UserRole.SuperAdmin, UserRole.RegionManager],

  // Region Management - SuperAdmin, RegionManager
  [ROUTES.REGIONS]: [UserRole.SuperAdmin, UserRole.RegionManager],

  // State Management - SuperAdmin, RegionManager
  [ROUTES.STATES]: [UserRole.SuperAdmin, UserRole.RegionManager],

  // History - SuperAdmin, RegionManager, AccountManager
  [ROUTES.HISTORY]: [UserRole.SuperAdmin, UserRole.RegionManager, UserRole.AccountManager],

  // User Management - SuperAdmin, RegionManager
  [ROUTES.USER_MANAGEMENT]: [UserRole.SuperAdmin, UserRole.RegionManager],

  // School Management (Alternative routes)
  [ROUTES.ADD_SCHOOL]: [UserRole.Admin, UserRole.SuperAdmin, UserRole.RegionManager],
};

/**
 * Helper function to match dynamic routes (e.g., /accounts/:accountId)
 */
export const matchDynamicRoute = (actualPath: string, pattern: string): boolean => {
  const actualParts = actualPath.split('/');
  const patternParts = pattern.split('/');

  if (actualParts.length !== patternParts.length) {
    return false;
  }

  return patternParts.every((part, index) => {
    if (part.startsWith(':')) {
      return true; // Parameter matches anything
    }
    return part === actualParts[index];
  });
};

/**
 * Check if a user role has access to a specific route
 */
export const hasAccessToRoute = (path: string, userRole: string): boolean => {
  // Check exact match first
  let allowedRoles = ROUTE_ACCESS_RULES[path];

  // If no exact match, check for dynamic routes
  if (!allowedRoles) {
    for (const [routePattern, roles] of Object.entries(ROUTE_ACCESS_RULES)) {
      if (routePattern.includes(':') && matchDynamicRoute(path, routePattern)) {
        allowedRoles = roles;
        break;
      }
    }
  }

  if (!allowedRoles) return true; // Allow access to undefined routes for now

  return allowedRoles.includes(userRole as UserRole);
};

export default ROUTES;
